import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import store from '../../../redux/store';
import { updateOpenModals } from '../../../redux/Slice/gridSlice';
import {
  fetch_library_page, reset_library,
  fetch_sketchfab_page, reset_sketchfab,
} from '../../../redux/Slice/LiabrarySlice';
import { iconsPath } from '../../../utils/imagePath';
import { ChevronDown } from 'lucide-react';
import vector from '../../../assets/images/icons/Vector.svg';
import MeshPreviewViewer from '../../../pages/Training/components/MeshPreviewViewer';
import CreateProjectModal from '../../ProjectCreation/CreateNewProject';

// ── constants ──
const SIDEBAR_CATEGORIES = [
  { label: '3D Assets', value: '3D'        },
  { label: 'Templates', value: 'TEMPLATES' },
  { label: 'Scenes',    value: 'SCENES'    },
  { label: 'Images',    value: 'IMAGES'    },
  { label: 'Video',     value: 'VIDEO'     },
  { label: 'Audio',     value: 'AUDIO'     },
  { label: 'Text',      value: 'TEXT'      },
  { label: 'Vectors',   value: 'VECTORS'   },
  { label: 'Sprites',   value: 'SPRITES'   },
];

const INDUSTRY_OPTIONS = [
  'Energy and Mining','Manufacturing & Industrial','Healthcare and Pharma',
  'Retail & E-Commerce','Education & Training','Automotive & Transportation',
  'Architecture & Real Estate','Defense & Aerospace','Tourism & Hospitality',
];
const CATEGORY_OPTIONS = ['Scene','Object','Character','Effect'];
const ASSET_SIZE_OPTIONS = [
  { label:'< 1 MB',          minBytes:0,          maxBytes:1048576    },
  { label:'1 – 5 MB',        minBytes:1048576,     maxBytes:5242880    },
  { label:'5 – 20 MB',       minBytes:5242880,     maxBytes:20971520   },
  { label:'20 – 100 MB',     minBytes:20971520,    maxBytes:104857600  },
  { label:'100 MB – 500 MB', minBytes:104857600,   maxBytes:524288000  },
  { label:'500 MB – 2 GB',   minBytes:524288000,   maxBytes:2147483648 },
  { label:'2 GB – 5 GB',     minBytes:2147483648,  maxBytes:5368709120 },
  { label:'5 GB+',           minBytes:5368709120,  maxBytes:Infinity   },
];
const SORT_OPTIONS = [
  { value:'relevance',   label:'Relevance'    },
  { value:'spatialgrid', label:'Spatial Grid' },
  { value:'sketchfab',   label:'Sketchfab'    },
];

const formatFileSize = (bytes) => {
  if (bytes == null) return null;
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576)    return `${Math.round(bytes / 1048576)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
};
const formatPolyCount = (n) => {
  if (n == null) return null;
  return n >= 1000 ? `${Math.round(n / 1000)}k poly` : `${n} poly`;
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `Updated ${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `Updated ${h}h ago`;
  return `Updated ${Math.floor(h / 24)}d ago`;
};

// ── CheckboxRow ───────────────────────────────────────────────────────────────
const CheckboxRow = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2.5 cursor-pointer select-none"
    onClick={e => { e.stopPropagation(); onChange(); }}>
    <div className={`flex-shrink-0 w-[14px] h-[14px] border rounded-sm flex items-center justify-center transition-colors
      ${checked ? 'bg-red-600 border-red-600' : 'border-[#555] bg-transparent'}`}>
      {checked && <svg className="w-2 h-2 text-white" viewBox="0 0 10 8" fill="none">
        <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>}
    </div>
    <span className="text-[12px] text-[#ccc]">{label}</span>
  </label>
);

// ── FilterPanel ───────────────────────────────────────────────────────────────
const FilterPanel = ({ onClose, onApply, initialFilters, anchorRef }) => {
  const [industries, setIndustries] = useState(initialFilters?.industries || []);
  const [categories, setCategories] = useState(initialFilters?.categories || []);
  const [assetSizes, setAssetSizes]  = useState(initialFilters?.assetSizes  || []);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const toggle = (setList, v) => setList(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v]);

  useEffect(() => {
    if (anchorRef?.current) {
      const r = anchorRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 8, right: window.innerWidth - r.right });
    }
  }, [anchorRef]);

  return createPortal(
    <div className="fixed inset-0 z-[9999999]"
      onMouseDown={e => { e.stopPropagation(); onClose(); }}>
      <div className="absolute rounded-xl bg-[#1E1E22] border border-[#3A3A3E] shadow-2xl flex flex-col"
        style={{ top: pos.top, right: pos.right, width: 680, maxHeight: 480 }}
        onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
        <button type="button" onClick={e => { e.stopPropagation(); onClose(); }}
          className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full border border-[#444] text-[#888] hover:text-white transition-colors text-[13px]">✕</button>
        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-3 gap-6">
            <div>
              <p className="text-[11px] font-semibold text-[#666] uppercase tracking-widest mb-3">Industry</p>
              <div className="flex flex-col gap-2.5">
                {INDUSTRY_OPTIONS.map(o => <CheckboxRow key={o} label={o} checked={industries.includes(o)} onChange={() => toggle(setIndustries, o)}/>)}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-[#666] uppercase tracking-widest mb-3">Categories</p>
              <div className="flex flex-col gap-2.5">
                {CATEGORY_OPTIONS.map(o => <CheckboxRow key={o} label={o} checked={categories.includes(o)} onChange={() => toggle(setCategories, o)}/>)}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-[#666] uppercase tracking-widest mb-3">Asset Size</p>
              <div className="flex flex-col gap-2.5">
                {ASSET_SIZE_OPTIONS.map(o => <CheckboxRow key={o.label} label={o.label} checked={assetSizes.includes(o.label)} onChange={() => toggle(setAssetSizes, o.label)}/>)}
              </div>
            </div>
          </div>
        </div>
        <div className="flex-shrink-0 border-t border-[#2a2a2e] px-5 py-3 flex items-center justify-between">
          <button type="button" onClick={e => { e.stopPropagation(); setIndustries([]); setCategories([]); setAssetSizes([]); }}
            className="text-[12px] text-[#777] hover:text-white">Reset all</button>
          <div className="flex gap-2">
            <button type="button" onClick={e => { e.stopPropagation(); onClose(); }}
              className="px-4 py-1.5 rounded-md border border-[#444] text-white text-[12px] hover:bg-[#2a2a2e]">Cancel</button>
            <button type="button"
              onClick={e => { e.stopPropagation(); onApply({ industries, categories, assetSizes }); onClose(); }}
              className="px-4 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-[12px] font-medium">Apply Filters</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Add to Project Modal ───
const AddToProjectModal = ({ asset, onClose }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [confirmProject, setConfirmProject] = useState(null);
  const [adding, setAdding] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);

  const loadProjects = () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    fetch(
      `${process.env.REACT_APP_BASE_URL}experience?page=1&limit=100&sortBy=created_at&order=desc`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((r) => r.json())
      .then((res) => setProjects(res?.success ? (res.data?.data || res.data || []) : []))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadProjects(); }, []);

  const filtered = projects.filter((p) =>
    (p.project_name || p.name || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleConfirmAdd = async () => {
    if (!confirmProject || adding) return;
    setAdding(true);
    try {
      const isSketchfab = asset.source === 'sketchfab';
      const isCurrentProject = window.location.pathname.includes(confirmProject._id);

      if (isSketchfab) {
        // ── Sketchfab: call Sketchfab download API to get the GLB URL ──────
        if (!asset.isDownloadable) {
          toast.error('This Sketchfab model is not available for download.');
          setAdding(false);
          return;
        }
        let downloadUrl;
        const toastId = toast.loading('Fetching Sketchfab download URL…');
        try {
          const { sketchfabService } = await import('../../../utils/automation/sketchfabService');
          downloadUrl = await sketchfabService.getModelDownloadUrl(asset.uid);
          toast.dismiss(toastId);
        } catch (e) {
          toast.dismiss(toastId);
          console.error('Sketchfab download URL error:', e);
          toast.error(`Could not get download link: ${e.message}`);
          setAdding(false);
          return;
        }

        if (!downloadUrl) {
          toast.error('Sketchfab returned no download URL for this model.');
          setAdding(false);
          return;
        }

        const assetData = {
          ...asset,
          url:    downloadUrl,
          title:  asset.name,
          name:   asset.name,
          source: 'sketchfab',
          type:   '3D',
        };

        if (isCurrentProject) {
          // Already on this project - directly import, then close all modals
          setConfirmProject(null);
          onClose(); // Close AddToProjectModal
          // Close entire library modal
          store.dispatch(updateOpenModals({ name: 'library', isOpen: false }));
          const { handleLibraryAssetImport } = await import('../../../components/gridpagefunction/LibraryAsset');
          const { scene, transformControls, setPolyCount } = window.gridPageSceneObjects || {};
          if (scene) {
            await handleLibraryAssetImport(assetData, { scene, transformControls, setPolyCount });
          } else {
            console.warn('Scene not ready, falling back to navigation');
            navigate(`/gridpage/${confirmProject._id}`, { state: { pendingLibraryAsset: assetData } });
          }
        } else {
          navigate(`/gridpage/${confirmProject._id}`, { state: { pendingLibraryAsset: assetData } });
        }
      } else {
        const token = localStorage.getItem('token');
        await fetch(`${process.env.REACT_APP_BASE_URL}assets/library/add`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ projectId: confirmProject._id, libraryAssetId: asset._id }),
        });

        if (isCurrentProject) {
          // Already on this project - directly import, then close all modals
          setConfirmProject(null);
          onClose(); // Close AddToProjectModal
          // Close entire library modal
          store.dispatch(updateOpenModals({ name: 'library', isOpen: false }));
          const { handleLibraryAssetImport } = await import('../../../components/gridpagefunction/LibraryAsset');
          const { scene, transformControls, setPolyCount } = window.gridPageSceneObjects || {};
          if (scene) {
            await handleLibraryAssetImport(asset, { scene, transformControls, setPolyCount });
          } else {
            console.warn('Scene not ready, falling back to navigation');
            navigate(`/gridpage/${confirmProject._id}`, { state: { pendingLibraryAsset: asset } });
          }
        } else {
          navigate(`/gridpage/${confirmProject._id}`, { state: { pendingLibraryAsset: asset } });
        }
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to add asset to project');
    } finally {
      setAdding(false);
    }
  };

  return createPortal(
    <>
      {showCreateProject && (
        <div className="fixed inset-0 z-[100001] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <CreateProjectModal
            closeModal={() => setShowCreateProject(false)}
            onProjectCreated={(newProject) => {
              setProjects((prev) => [newProject, ...prev]);
              setShowCreateProject(false);
              setConfirmProject(newProject);
            }}
          />
        </div>
      )}
      <div className="fixed inset-0 z-[99999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
        <div
          className="relative flex flex-col w-full max-w-[1200px] max-h-[92vh] rounded-xl overflow-hidden"
          style={{ border: '1px solid #2a2a2e', background: '#141416', boxShadow: '0 25px 60px rgba(0,0,0,0.6)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex-shrink-0 flex items-center justify-between px-4 sm:px-8 pt-5 sm:pt-7 pb-4 sm:pb-5">
            <div className="flex items-center gap-3 sm:gap-4">
              <button type="button" onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-full border border-[#3a3a3e] text-white hover:border-white transition-colors">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                  <path d="M19 12H5M12 5l-7 7 7 7" />
                </svg>
              </button>
              <div>
                <h2 className="text-[16px] sm:text-[20px] font-bold text-white leading-tight">Add to project</h2>
                <p className="text-[12px] sm:text-[13px] text-[#666] mt-0.5 hidden sm:block">Choose an existing project or create a new project</p>
              </div>
            </div>
            <button type="button" onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-[#3a3a3e] text-[#aaa] hover:text-white hover:border-white transition-colors text-[16px]">✕</button>
          </div>

          <div className="flex-shrink-0 flex items-center gap-3 px-4 sm:px-8 pb-4 sm:pb-8">
            <div className="relative flex-1">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input type="text" placeholder="Search by project name"
                value={search} onChange={(e) => setSearch(e.target.value)}
                className="w-full h-[44px] sm:h-[50px] pl-11 pr-5 rounded-full bg-[#1e1f22] border border-[#2a2a2e] text-[13px] sm:text-[14px] text-white placeholder:text-[#444] focus:outline-none focus:border-[#444]" />
            </div>
            <button type="button" onClick={() => setShowCreateProject(true)}
              className="flex-shrink-0 flex items-center gap-2 px-4 sm:px-6 h-[44px] sm:h-[50px] rounded-[5px] bg-red-600 hover:bg-red-700 text-white text-[13px] sm:text-[14px] font-semibold transition-colors whitespace-nowrap">
              <span className="text-[18px] leading-none font-light">+</span>
              <span className="hidden sm:inline">New project</span>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar px-4 sm:px-8 pb-6 sm:pb-8" style={{ minHeight: 0 }}>
            {loading ? (
              <div className="flex items-center justify-center h-40 text-[#555] text-[13px] gap-2">
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                Loading projects…
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 gap-2">
                <p className="text-[#555] text-[13px]">No projects found</p>
                {search && <button type="button" onClick={() => setSearch('')} className="text-[12px] text-red-500 hover:text-red-400">Clear search</button>}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4 sm:gap-5">
                {filtered.map((project) => {
                  const name = project.project_name || project.name || 'Untitled';
                  const thumb = project.th_image_url || project.thumbnail || project.coverImage;
                  const tags = [project.category, project.experience_type].filter(Boolean);
                  const isSelected = confirmProject?._id === project._id;
                  return (
                    <div key={project._id}
                      onClick={() => setConfirmProject(isSelected ? null : project)}
                      className={`flex flex-col rounded-xl overflow-hidden bg-[#1e1f22] cursor-pointer transition-all duration-150 ${isSelected ? 'ring-2 ring-red-500' : 'hover:ring-1 hover:ring-[#EE2B2A]'}`}>
                      <div className="w-full bg-[#111114] overflow-hidden aspect-video">
                        <img src={thumb} alt={name} className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = 'https://spatialgrid-uploads.s3.ap-south-1.amazonaws.com/uploads/library-assets/default-surface-thumbnail.png'; }} />
                      </div>
                      <div className="px-3 sm:px-4 py-3 flex flex-col gap-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[12px] sm:text-[13px] font-bold text-white truncate">{name}</span>
                          <span className="text-[10px] sm:text-[11px] text-[#555] flex-shrink-0 hidden sm:block">{timeAgo(project.updatedAt || project.createdAt)}</span>
                        </div>
                        {tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {tags.map((t, ti) => (
                              <span key={`${project._id}-tag-${ti}`} className="px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] bg-[#2a2a2e] border border-[#333] text-[#888] capitalize">{t}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Confirm overlay */}
          {confirmProject && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 rounded-xl p-4"
              onClick={() => setConfirmProject(null)}>
              <div className="relative flex flex-col items-center text-center rounded-2xl bg-[#1e1f22] px-6 sm:px-10 py-8 sm:py-10 shadow-2xl w-full max-w-[420px]"
                onClick={(e) => e.stopPropagation()}>
                <button type="button" onClick={() => setConfirmProject(null)}
                  className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full border border-[#3a3a3e] text-[#aaa] hover:text-white hover:border-white transition-colors text-[14px]">✕</button>
                <div className="w-[100px] h-[100px] sm:w-[140px] sm:h-[140px] rounded-full overflow-hidden bg-[#2a2a2e] border-2 border-[#3a3a3e] mb-5 sm:mb-6 flex items-center justify-center">
                  <img src={asset.thumbnail} alt={asset.name} className="w-full h-full object-contain"
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = 'https://spatialgrid-uploads.s3.ap-south-1.amazonaws.com/uploads/library-assets/default-surface-thumbnail.png'; }} />
                </div>
                <h3 className="text-[18px] sm:text-[22px] font-bold text-white mb-3 leading-tight">Add {asset.name} to Project?</h3>
                <p className="text-[13px] sm:text-[14px] text-[#888] leading-relaxed mb-6 sm:mb-8">
                  Are you sure you want to add <span className="text-white font-medium">'{asset.name}'</span> to this project?
                  This asset will be linked to the selected project and can be edited later.
                </p>
                <div className="flex gap-3 w-full">
                  <button type="button" onClick={() => setConfirmProject(null)}
                    className="flex-1 py-3 sm:py-3.5 rounded-xl bg-[#2a2a2e] text-white text-[14px] sm:text-[15px] font-semibold hover:bg-[#333] transition-colors">Cancel</button>
                  <button type="button" disabled={adding} onClick={handleConfirmAdd}
                    className="flex-1 py-3 sm:py-3.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-[14px] sm:text-[15px] font-semibold transition-colors disabled:opacity-60">
                    {adding ? 'Adding…' : 'Continue'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

// ── Asset Detail Modal (for SpatialGrid assets) ───────────────────────────────
const AssetDetailModal = ({ asset, onClose }) => {
  const [adding, setAdding] = useState(false);
  if (!asset) return null;

  const modelUrl = asset.streamUrl || asset.url || asset.asset_file_path || null;
  const is3D = !!(modelUrl && (
    modelUrl.toLowerCase().includes('.glb') ||
    modelUrl.toLowerCase().includes('.gltf') ||
    asset.type === '3D' ||
    asset.type === '3d'
  ));

  const techRows = [
    { label: 'Technique', value: asset.type ? `${asset.type} Model` : '3D Model' },
    { label: 'File Size',  value: formatFileSize(asset.metadata?.fileSize) || '—' },
    { label: 'Polycount',  value: formatPolyCount(asset.metadata?.polyCount) || '—' },
    { label: 'Format',     value: asset.metadata?.format?.toUpperCase() || '—' },
  ];

  const handleAddToGridPage = async () => {
    if (adding) return;
    setAdding(true);
    try {
      // Close detail modal
      onClose();
      // Close entire library modal
      store.dispatch(updateOpenModals({ name: 'library', isOpen: false }));
      
      // Import and use LibraryAsset handler to add directly to scene
      const { handleLibraryAssetImport } = await import('../../../components/gridpagefunction/LibraryAsset');
      const { scene, transformControls, setPolyCount } = window.gridPageSceneObjects || {};
      
      if (scene) {
        await handleLibraryAssetImport(asset, { scene, transformControls, setPolyCount });
      } else {
        toast.error('Scene not ready. Please try again.');
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to add asset to scene');
    } finally {
      setAdding(false);
    }
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9999999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
        <div
          className="relative flex flex-col w-full max-w-[1200px] rounded-xl bg-[#1a1a1e] shadow-2xl overflow-hidden"
          style={{ height: 'min(85vh, 780px)', minHeight: 400 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex-shrink-0 flex items-center justify-between px-5 sm:px-7 pt-4 sm:pt-5 pb-3 sm:pb-4 border-b border-[#2a2a2e]">
            <h2 className="text-[16px] sm:text-[20px] font-semibold text-white truncate pr-4">{asset.name}</h2>
            <button type="button" onClick={onClose}
              className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full border border-[#555] text-[#aaa] hover:text-white hover:border-white transition-colors text-[16px]">✕</button>
          </div>

          <div className="flex flex-col md:flex-row flex-1 min-h-0 gap-4 p-4">
            <div className="flex-[6] relative bg-[#080808] rounded-xl overflow-hidden min-h-[220px] md:min-h-0">
              {is3D ? (
                <MeshPreviewViewer url={modelUrl} />
              ) : (
                <img src={asset.thumbnail} alt={asset.name} className="w-full h-full object-contain"
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = 'https://spatialgrid-uploads.s3.ap-south-1.amazonaws.com/uploads/library-assets/default-surface-thumbnail.png'; }} />
              )}
            </div>

            <div className="flex-[4] flex flex-col min-h-0 border border-[#3a3a3e] rounded-xl overflow-hidden">
              <div className="flex-1 overflow-y-auto custom-scrollbar px-4 sm:px-6 py-4 sm:py-5 flex flex-col gap-4 sm:gap-5">
                <p className="text-[11px] font-semibold tracking-widest text-[#888] uppercase">Asset Details</p>
                <div>
                  <p className="text-[13px] text-[#aaa] mb-2">Category</p>
                  <span className="inline-block px-3 py-1 rounded-md bg-[#2a2a2e] border border-[#3a3a3e] text-[12px] text-white capitalize">
                    {asset.subtype || asset.type || '—'}
                  </span>
                </div>
                {asset.tags?.length > 0 && (
                  <div>
                    <p className="text-[13px] text-[#aaa] mb-2">Keywords</p>
                    <div className="flex flex-wrap gap-2">
                      {asset.tags.map((tag) => (
                        <span key={tag} className="px-2.5 py-1 rounded-md bg-[#2a2a2e] border border-[#3a3a3e] text-[12px] text-white capitalize">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-[15px] font-semibold text-white mb-3">Technical information</p>
                  <table className="w-full border-collapse rounded-lg overflow-hidden">
                    <tbody>
                      {techRows.map((row, i) => (
                        <tr key={row.label} className={i % 2 === 0 ? 'bg-[#222226]' : 'bg-[#1e1e22]'}>
                          <td className="px-3 py-2.5 text-[12px] text-[#aaa] w-[45%]">{row.label}</td>
                          <td className="px-3 py-2.5 text-[12px] text-white font-medium">{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-t border-[#2a2a2e] bg-[#1a1a1e]">
                <button type="button" disabled={adding} onClick={handleAddToGridPage}
                  className="w-full py-2.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-[14px] font-semibold transition-colors disabled:opacity-60">
                  {adding ? 'Adding…' : 'Add to Project'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

// ── Sketchfab Detail Modal ────────────────────────────────────────────────────
const SketchfabDetailModal = ({ model, onClose }) => {
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [showAddToProject, setShowAddToProject] = useState(false);
  if (!model) return null;

  const isPaid = model.isPaid === true || model.staffpickedAt != null;
  const thumbnail = model.thumbnail || model.thumbnails?.images?.[0]?.url || '';

  // ── "Add to Project" — open project picker ──────────────────────────────
  // We pass a skeletal asset; the real download URL is resolved inside
  // AddToProjectModal just before import so the user sees the project picker
  // immediately without waiting for Sketchfab.
  const assetForProject = {
    _id:       model.uid,
    uid:       model.uid,
    name:      model.name,
    thumbnail,
    source:    'sketchfab',
    isDownloadable: model.isDownloadable,
    ...model,
  };

  // ── "Add to Project" directly (no project picker — current page is gridpage)
  const handleAddDirectly = async () => {
    if (adding) return;
    if (!model.isDownloadable) {
      toast.error('This model is not available for download.');
      return;
    }
    setAdding(true);
    const toastId = toast.loading('Fetching model download URL…');
    try {
      const { sketchfabService } = await import('../../../utils/automation/sketchfabService');
      const downloadUrl = await sketchfabService.getModelDownloadUrl(model.uid);
      if (!downloadUrl) throw new Error('No download URL returned');
      toast.loading('Loading model into scene…', { id: toastId });

      const assetData = {
        ...assetForProject,
        url:    downloadUrl,
        source: 'sketchfab',
        type:   '3D',
      };

      onClose();
      store.dispatch(updateOpenModals({ name: 'library', isOpen: false }));

      const { handleLibraryAssetImport } = await import('../../../components/gridpagefunction/LibraryAsset');
      const { scene, transformControls, setPolyCount } = window.gridPageSceneObjects || {};
      if (scene) {
        await handleLibraryAssetImport(assetData, { scene, transformControls, setPolyCount });
        toast.success('Model added to scene!', { id: toastId });
      } else {
        toast.dismiss(toastId);
        toast.error('Scene not ready. Please try again.');
      }
    } catch (e) {
      console.error('Sketchfab direct add error:', e);
      toast.error(`Failed: ${e.message}`, { id: toastId });
    } finally {
      setAdding(false);
    }
  };

  // Decide which action the button takes:
  // - If we're already on a gridpage → load directly into scene
  // - Otherwise → open the project picker modal
  const isOnGridPage = window.location.pathname.includes('/gridpage/');
  const handleAddToProject = isOnGridPage ? handleAddDirectly : () => setShowAddToProject(true);

  return createPortal(
    <>
      {showAddToProject && (
        <AddToProjectModal asset={assetForProject} onClose={() => setShowAddToProject(false)} />
      )}
      <div className="fixed inset-0 z-[9999999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
        <div
          className="relative flex flex-col w-full max-w-[1200px] rounded-xl bg-[#1a1a1e] shadow-2xl overflow-hidden"
          style={{ height: 'min(85vh, 790px)', minHeight: 400 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex-shrink-0 flex items-center justify-between px-5 sm:px-8 pt-5 sm:pt-6 pb-3 sm:pb-4 border-b border-[#2a2a2e]">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <h2 className="text-[16px] sm:text-[22px] font-semibold text-white truncate">{model.name}</h2>
              {isPaid ? (
                <div className="flex-shrink-0 flex items-center gap-1.5 px-2 sm:px-3 py-1 rounded-full bg-[#2a2a2e] border border-[#3a3a3e]">
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-[#F5A623]"><path d="M2 19l2-8 4 4 4-8 4 8 4-4 2 8H2z"/></svg>
                  <span className="text-[10px] sm:text-[11px] text-[#F5A623] font-semibold">Premium</span>
                </div>
              ) : (
                <span className="flex-shrink-0 px-2 sm:px-2.5 py-0.5 rounded-full bg-green-500/10 text-[10px] sm:text-[11px] text-green-400 font-semibold">Free</span>
              )}
            </div>
            <button type="button" onClick={onClose}
              className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full border border-[#555] text-[#aaa] hover:text-white hover:border-white transition-colors text-[16px]">✕</button>
          </div>

          <div className="flex flex-col md:flex-row flex-1 min-h-0 gap-4 p-4">
            <div className="flex-[6] relative bg-[#080808] rounded-xl overflow-hidden min-h-[220px] md:min-h-0">
              <iframe src={model.embedUrl} title={model.name} className="w-full h-full"
                allow="autoplay; fullscreen; xr-spatial-tracking" allowFullScreen style={{ border: 0 }} />
              <div className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm text-[11px] text-[#aaa] font-medium pointer-events-none">
                Powered by Sketchfab
              </div>
            </div>

            <div className="flex-[4] flex flex-col min-h-0 border border-[#3a3a3e] rounded-xl overflow-hidden">
              <div className="flex-1 overflow-y-auto custom-scrollbar px-4 sm:px-6 py-4 sm:py-5 flex flex-col gap-4 sm:gap-5">
                <p className="text-[11px] font-semibold tracking-widest text-[#888] uppercase">Model Details</p>
                {model.author && (
                  <div>
                    <p className="text-[13px] text-[#aaa] mb-2">Author</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#2a2a2e] border border-[#3a3a3e] flex items-center justify-center text-[12px] text-white font-semibold">
                        {(model.author.displayName ?? model.author.username ?? 'U')[0].toUpperCase()}
                      </div>
                      <span className="text-[13px] text-white font-medium">
                        {model.author.displayName ?? model.author.username ?? 'Unknown'}
                      </span>
                    </div>
                  </div>
                )}
                {model.license && (
                  <div>
                    <p className="text-[13px] text-[#aaa] mb-2">License</p>
                    <span className="inline-block px-3 py-1 rounded-md bg-[#2a2a2e] border border-[#3a3a3e] text-[12px] text-white">{model.license}</span>
                  </div>
                )}
                {model.tags?.length > 0 && (
                  <div>
                    <p className="text-[13px] text-[#aaa] mb-2">Tags</p>
                    <div className="flex flex-wrap gap-2">
                      {model.tags.slice(0, 12).map((tag, i) => (
                        <span key={i} className="px-2.5 py-1 rounded-md bg-[#2a2a2e] border border-[#3a3a3e] text-[12px] text-white lowercase">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-[15px] font-semibold text-white mb-3">Statistics</p>
                  <table className="w-full border-collapse rounded-lg overflow-hidden">
                    <tbody>
                      {[
                        { label: 'Views',        value: model.viewCount?.toLocaleString()    ?? '—' },
                        { label: 'Likes',        value: model.likeCount?.toLocaleString()    ?? '—' },
                        { label: 'Downloads',    value: model.downloadCount?.toLocaleString() ?? '—' },
                        { label: 'Downloadable', value: model.isDownloadable ? 'Yes' : 'No' },
                      ].map((row, i) => (
                        <tr key={row.label} className={i % 2 === 0 ? 'bg-[#222226]' : 'bg-[#1e1e22]'}>
                          <td className="px-3 py-2.5 text-[12px] text-[#aaa] w-[45%]">{row.label}</td>
                          <td className="px-3 py-2.5 text-[12px] text-white font-medium">{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {model.description && (
                  <div>
                    <p className="text-[15px] font-semibold text-white mb-2">Description</p>
                    <p className="text-[12px] text-[#aaa] leading-relaxed whitespace-pre-wrap">{model.description}</p>
                  </div>
                )}
              </div>
              <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-t border-[#2a2a2e] bg-[#1a1a1e]">
                <button
                  type="button"
                  disabled={adding || !model.isDownloadable}
                  onClick={handleAddToProject}
                  className="w-full py-2.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-[14px] font-semibold transition-colors disabled:opacity-60"
                  title={!model.isDownloadable ? 'This model is not downloadable' : undefined}
                >
                  {adding ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                      Fetching model…
                    </span>
                  ) : !model.isDownloadable ? 'Not Downloadable' : isOnGridPage ? 'Add to Scene' : 'Add to Project'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

// ── LibraryAssetCard — matches Library.jsx card design exactly ────────────────
const LibraryAssetCard = ({ asset, onSelect }) => {
  const name  = asset.name || asset.title || 'Untitled';
  const thumb = asset.thumbnail || asset.streamUrl;
  const poly  = formatPolyCount(asset.metadata?.polyCount ?? asset.faceCount);
  const size  = formatFileSize(asset.metadata?.fileSize);
  const meta  = [poly, size].filter(Boolean).join(' • ');

  return (
    <div
      className="relative flex flex-col rounded-xl overflow-hidden bg-[#1e1f22] cursor-pointer hover:ring-1 hover:ring-[#EE2B2A] transition-all duration-150"
      onClick={() => onSelect(asset)}
    >
      <div className="relative w-full overflow-hidden p-3" style={{ height: 170 }}>
        <div className="relative w-full h-full rounded-[8px] overflow-hidden">
          <img src={thumb} alt={name} className="w-full h-full object-cover"
            onError={e => {
              if (e.currentTarget.src !== asset.streamUrl && asset.streamUrl) {
                e.currentTarget.src = asset.streamUrl;
              } else {
                e.currentTarget.onerror = null;
                e.currentTarget.src = 'https://spatialgrid-uploads.s3.ap-south-1.amazonaws.com/uploads/library-assets/default-surface-thumbnail.png';
              }
            }}/>
        </div>
      </div>
      <div className="px-3 pb-3 flex flex-col gap-0.5">
        <span className="text-[12px] font-bold text-white truncate">{name}</span>
        {meta && <span className="text-[10px] text-[#666]">{meta}</span>}
      </div>
    </div>
  );
};

// ── SketchfabCard — matches Library.jsx SketchfabCard design exactly ────=
const SketchfabCard = ({ model, onSelect }) => {
  const isPaid = model.isPaid === true || model.staffpickedAt != null;
  const thumb  = model.thumbnails?.images?.[0]?.url ?? model.thumbnail ?? '';
  const name   = model.name ?? 'Untitled';
  const poly   = formatPolyCount(model.faceCount);
  const size   = formatFileSize(model.archiveSize);
  const meta   = [poly, size].filter(Boolean).join(' • ');

  return (
    <div
      className="relative flex flex-col rounded-xl overflow-hidden bg-[#1e1f22] cursor-pointer hover:ring-1 hover:ring-[#EE2B2A] transition-all duration-150"
      onClick={() => onSelect?.(model)}
    >
      <div className="relative w-full overflow-hidden p-3" style={{ height: 170 }}>
        <div className="relative w-full h-full rounded-[8px] overflow-hidden">
          <img src={thumb} alt={name} className="w-full h-full object-cover"
            onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = 'https://spatialgrid-uploads.s3.ap-south-1.amazonaws.com/uploads/library-assets/default-surface-thumbnail.png'; }}/>
          {isPaid ? (
            <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-[#1e1f22]/80 flex items-center justify-center shadow-lg">
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#F5A623]"><path d="M2 19l2-8 4 4 4-8 4 8 4-4 2 8H2z"/></svg>
            </div>
          ) : (
            <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-[#1e1f22]/80 text-[10px] font-semibold text-green-400 shadow-lg">Free</div>
          )}
        </div>
      </div>
      <div className="px-3 pb-3 flex flex-col gap-0.5">
        <span className="text-[12px] font-bold text-white truncate">{name}</span>
        {meta && <span className="text-[10px] text-[#666]">{meta}</span>}
      </div>
    </div>
  );
};

// ── helpers ────
const Spinner = () => (
  <div className="flex justify-center py-5">
    <svg className="w-5 h-5 animate-spin text-[#555]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
    </svg>
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-semibold text-[#555] uppercase tracking-widest mb-3">{children}</p>
);

const SkeletonGrid = ({ cols = 4, count = 8, height = 210 }) => (
  <div className={`grid grid-cols-${cols} gap-3`}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="rounded-xl bg-[#1e1f22] animate-pulse" style={{ height }}/>
    ))}
  </div>
);

// ── LibrarySection ───
const LibrarySection = ({ objectt }) => {
  const { openModals } = useSelector(s => s.assets_object);
  const {
    liabrary_cards, liabrary_loading, liabrary_loading_more,
    sketchfab_cards, sketchfab_loading, sketchfab_loading_more,
    sketchfab_has_more, sketchfab_cursor,
    has_more, current_page,
  } = useSelector(s => s.liabrary);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const [selectedCategory, setSelectedCategory] = useState('3D');
  const [searchQuery,    setSearchQuery]    = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [sortBy,         setSortBy]         = useState('relevance');
  const [isSortOpen,     setIsSortOpen]     = useState(false);
  const [filterOpen,     setFilterOpen]     = useState(false);
  const [activeFilters,  setActiveFilters]  = useState(null);
  const [selectedAsset,  setSelectedAsset]  = useState(null);

  const filterBtnRef  = useRef(null);
  const sfFetchingRef = useRef(false);
  const sfSentinelRef = useRef(null);
  const fetchingRef   = useRef(false);
  const sentinelRef   = useRef(null);

  const isSketchfab   = sortBy === 'sketchfab';
  const isSpatialgrid = sortBy === 'spatialgrid';
  const isRelevance   = sortBy === 'relevance';
  const showLibrary   = isRelevance || isSpatialgrid;
  // Sketchfab is ONLY for 3D Assets category
  const showSketchfab = (isRelevance || isSketchfab) && selectedCategory === '3D';
  const sortLabel     = SORT_OPTIONS.find(o => o.value === sortBy)?.label ?? 'Relevance';
  const totalActiveFilters = activeFilters
    ? (activeFilters.industries?.length || 0) + (activeFilters.categories?.length || 0) + (activeFilters.assetSizes?.length || 0)
    : 0;

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(searchQuery), 600);
    return () => clearTimeout(id);
  }, [searchQuery]);

  const buildParams = useCallback((page = 1) => {
    const p = { page, limit: 24, type: selectedCategory };
    if (activeFilters?.categories?.length === 1) p.type = activeFilters.categories[0];
    if (activeFilters?.industries?.length)       p.industry = activeFilters.industries[0];
    if (activeFilters?.assetSizes?.length) {
      let min = Infinity, max = 0;
      activeFilters.assetSizes.forEach(label => {
        const o = ASSET_SIZE_OPTIONS.find(x => x.label === label);
        if (!o) return;
        if (o.minBytes < min) min = o.minBytes;
        if (o.maxBytes > max) max = o.maxBytes;
      });
      if (min !== Infinity) p.fileSizeMin = min;
      if (max > 0 && max !== Infinity) p.fileSizeMax = max;
    }
    if (debouncedQuery.trim()) p.search = debouncedQuery.trim();
    return p;
  }, [activeFilters, debouncedQuery, selectedCategory]);

  const buildSfParams = useCallback((cursor = null, isFirstPage = true) => {
    const q = [debouncedQuery.trim() || selectedCategory, activeFilters?.industries?.join(' ')].filter(Boolean).join(' ');
    return { q, categories: '', cursor, isFirstPage };
  }, [debouncedQuery, activeFilters, selectedCategory]);

  useEffect(() => {
    if (showLibrary) {
      fetchingRef.current = false;
      dispatch(reset_library());
      dispatch(fetch_library_page(buildParams(1)));
    }
    if (showSketchfab) {
      sfFetchingRef.current = false;
      dispatch(reset_sketchfab());
      dispatch(fetch_sketchfab_page(buildSfParams(null, true)));
    }
  }, [sortBy, debouncedQuery, activeFilters, selectedCategory, dispatch, buildParams, buildSfParams, showLibrary, showSketchfab]);

  useEffect(() => {
    if (!showLibrary) return;
    const el = sentinelRef.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && has_more && !liabrary_loading && !liabrary_loading_more && !fetchingRef.current) {
        fetchingRef.current = true;
        dispatch(fetch_library_page(buildParams(current_page + 1))).finally(() => { fetchingRef.current = false; });
      }
    }, { threshold: 0.1 });
    obs.observe(el); return () => obs.disconnect();
  }, [dispatch, buildParams, has_more, liabrary_loading, liabrary_loading_more, current_page, showLibrary]);

  useEffect(() => {
    if (!showSketchfab) return;
    const el = sfSentinelRef.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && sketchfab_has_more && !sketchfab_loading && !sketchfab_loading_more && !sfFetchingRef.current) {
        sfFetchingRef.current = true;
        dispatch(fetch_sketchfab_page(buildSfParams(sketchfab_cursor, false))).finally(() => { sfFetchingRef.current = false; });
      }
    }, { threshold: 0.1 });
    obs.observe(el); return () => obs.disconnect();
  }, [dispatch, buildSfParams, sketchfab_has_more, sketchfab_loading, sketchfab_loading_more, sketchfab_cursor, showSketchfab]);

  const closeModal = () => {
    setSearchQuery('');
    setSelectedAsset(null);
    store.dispatch(updateOpenModals({ name: 'library', isOpen: false }));
  };

  const handleLibraryAssetSelect = (asset) => {
    setSelectedAsset({ ...asset, source: 'spatialgrid' });
  };

  const handleSketchfabSelect = (model) => {
    setSelectedAsset({ ...model, source: 'sketchfab' });
  };

  if (!openModals?.library) return null;

  const libLoading = liabrary_loading && liabrary_cards.length === 0;
  const sfLoading  = sketchfab_loading && sketchfab_cards.length === 0;
  const CARD_GRID = 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3';

  return createPortal(
    <>
      {selectedAsset && selectedAsset.source === 'sketchfab' && (
        <SketchfabDetailModal model={selectedAsset} onClose={() => setSelectedAsset(null)} />
      )}
      {selectedAsset && selectedAsset.source !== 'sketchfab' && (
        <AssetDetailModal asset={selectedAsset} onClose={() => setSelectedAsset(null)} />
      )}

      {filterOpen && (
        <FilterPanel anchorRef={filterBtnRef} onClose={() => setFilterOpen(false)}
          onApply={f => {
            const empty = !f.industries.length && !f.categories.length && !f.assetSizes.length;
            setActiveFilters(empty ? null : f);
          }}
          initialFilters={activeFilters}
        />
      )}

      <div className="fixed inset-0 z-[999998] bg-black/70 flex items-center justify-center p-4"
        onClick={closeModal}>

        <div className="relative flex flex-col bg-[#131418] rounded-xl shadow-2xl overflow-hidden"
          style={{ width:'95vw', maxWidth:1140, height:'88vh', maxHeight:840 }}
          onClick={e => e.stopPropagation()}>

          <div className="flex-shrink-0 flex items-center gap-3 px-5 py-3.5 border-b border-[#222226]">
            <h2 className="text-[15px] font-semibold text-white mr-auto">Library Assets</h2>

            <div className="relative">
              <img className="absolute left-3 top-1/2 -translate-y-1/2 h-[13px] w-[13px] pointer-events-none" src={iconsPath?.group98Image} alt="search"/>
              <input type="text" placeholder="Search assets..."
                className="bg-[#1e1f22] text-white border border-[#2a2a2e] h-[34px] w-[240px] pl-9 pr-4 rounded-full focus:outline-none focus:border-[#444] placeholder:text-[#444] text-[13px]"
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}/>
            </div>

            <button ref={filterBtnRef} type="button"
              onClick={e => { e.stopPropagation(); setFilterOpen(p => !p); }}
              className={`relative flex items-center gap-1.5 h-[34px] px-3.5 rounded-full border text-[12px] transition-colors
                ${filterOpen || totalActiveFilters > 0 ? 'border-red-500 bg-red-600/10 text-white' : 'border-[#2a2a2e] bg-[#1e1f22] text-[#888] hover:text-white hover:border-[#444]'}`}>
              <img src={vector} alt="" className="w-3 h-3 opacity-60"/>
              <span>Filter</span>
              {totalActiveFilters > 0 && (
                <span className="ml-0.5 min-w-[16px] h-[16px] flex items-center justify-center rounded-full bg-red-600 text-[9px] font-bold text-white px-1">
                  {totalActiveFilters}
                </span>
              )}
            </button>

            <div className="relative">
              <button type="button"
                className="flex items-center gap-1.5 h-[34px] px-3.5 rounded-full border border-[#2a2a2e] bg-[#1e1f22] text-[12px] whitespace-nowrap hover:border-[#444] transition-colors"
                onClick={e => { e.stopPropagation(); setIsSortOpen(p => !p); }}>
                <span className="text-[#555]">Sort:</span>
                <span className="text-white font-medium">{sortLabel}</span>
                <ChevronDown size={12} className={`text-[#555] transition-transform ${isSortOpen ? 'rotate-180' : ''}`}/>
              </button>
              {isSortOpen && (
                <div className="absolute right-0 top-full mt-1.5 z-50 w-40 rounded-xl bg-[#1E1F22] border border-[#333] shadow-xl overflow-hidden"
                  onClick={e => e.stopPropagation()}>
                  {SORT_OPTIONS.map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => { setSortBy(opt.value); setIsSortOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 text-[12px] hover:bg-[#2a2a2e] transition-colors ${sortBy === opt.value ? 'text-white font-semibold' : 'text-[#888]'}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button onClick={closeModal}
              className="w-7 h-7 flex items-center justify-center rounded-full border border-[#2a2a2e] text-[#777] hover:text-white hover:border-[#444] transition-colors text-[14px]">✕</button>
          </div>

          {totalActiveFilters > 0 && (
            <div className="flex-shrink-0 flex flex-wrap items-center gap-1.5 px-5 py-2 border-b border-[#222226] bg-[#0f0f11]">
              {[
                ...(activeFilters?.industries || []).map(v => ({ type:'industries', label:v })),
                ...(activeFilters?.categories || []).map(v => ({ type:'categories', label:v })),
                ...(activeFilters?.assetSizes  || []).map(v => ({ type:'assetSizes',  label:v })),
              ].map(chip => (
                <span key={`${chip.type}-${chip.label}`}
                  className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] bg-[#222226] border border-[#333] text-[#ccc]">
                  {chip.label}
                  <button type="button" className="text-[#555] hover:text-white leading-none"
                    onClick={() => setActiveFilters(prev => {
                      const u = { ...prev, [chip.type]: prev[chip.type].filter(v => v !== chip.label) };
                      return (!u.industries.length && !u.categories.length && !u.assetSizes.length) ? null : u;
                    })}>✕</button>
                </span>
              ))}
              <button type="button" onClick={() => setActiveFilters(null)}
                className="text-[11px] text-red-500 hover:text-red-400 ml-1">Clear all</button>
            </div>
          )}

          <div className="flex flex-1 min-h-0 overflow-hidden">

            <div className="flex-shrink-0 w-[140px] flex flex-col gap-0.5 p-3 border-r border-[#222226] overflow-y-auto" style={{ scrollbarWidth:'none' }}>
              <p className="text-[10px] font-semibold text-[#555] uppercase tracking-widest px-2 mb-2">Categories</p>
              {SIDEBAR_CATEGORIES.map(cat => (
                <button key={cat.value} type="button"
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`w-full text-left px-3 py-2 rounded-full text-[12px] transition-colors
                    ${selectedCategory === cat.value ? 'bg-[#2a2a2e] text-white' : 'text-[#777] hover:bg-[#1e1f22] hover:text-white'}`}>
                  {cat.label}
                </button>
              ))}
            </div>

            <div className="flex-1 min-w-0 overflow-y-auto px-5 py-4" style={{ scrollbarWidth:'thin', scrollbarColor:'#333 transparent' }}>

              {showLibrary && (
                <div className={showSketchfab && sketchfab_cards.length > 0 ? 'mb-6' : ''}>
                  {isRelevance && sketchfab_cards.length > 0 && <SectionLabel>Spatial Grid</SectionLabel>}

                  {libLoading ? (
                    <SkeletonGrid cols={4} count={8} height={210}/>
                  ) : liabrary_cards.length > 0 ? (
                    <>
                      <div className={CARD_GRID}>
                        {liabrary_cards.map((a, i) => (
                          <LibraryAssetCard key={a._id ?? i} asset={a} onSelect={handleLibraryAssetSelect}/>
                        ))}
                      </div>
                      <div ref={sentinelRef} className="w-full h-4"/>
                      {liabrary_loading_more && <Spinner/>}
                      {!has_more && !liabrary_loading_more && liabrary_cards.length > 0 && (
                        <p className="text-center text-[11px] text-[#444] py-3">
                          Showing all {liabrary_cards.length} assets
                        </p>
                      )}
                    </>
                  ) : (
                    !liabrary_loading && (
                      <div className="flex flex-col items-center justify-center py-12 gap-2">
                        <p className="text-[#555] text-[13px]">No assets found</p>
                        {(activeFilters || debouncedQuery) && (
                          <button type="button" onClick={() => { setActiveFilters(null); setSearchQuery(''); }}
                            className="text-[12px] text-red-500 hover:text-red-400">Clear filters</button>
                        )}
                      </div>
                    )
                  )}
                </div>
              )}

              {showSketchfab && (
                <div>
                  {(isRelevance && liabrary_cards.length > 0) && <SectionLabel>From Sketchfab</SectionLabel>}
                  {isSketchfab && <SectionLabel>Sketchfab Models</SectionLabel>}

                  {sfLoading ? (
                    <SkeletonGrid cols={4} count={8} height={210}/>
                  ) : sketchfab_cards.length > 0 ? (
                    <>
                      <div className={CARD_GRID}>
                        {sketchfab_cards.map((m, i) => (
                          <SketchfabCard key={m.uid ?? i} model={m} onSelect={handleSketchfabSelect}/>
                        ))}
                      </div>
                      <div ref={sfSentinelRef} className="w-full h-4"/>
                      {sketchfab_loading_more && <Spinner/>}
                      {!sketchfab_has_more && !sketchfab_loading_more && sketchfab_cards.length > 0 && isSketchfab && (
                        <p className="text-center text-[11px] text-[#444] py-3">
                          Showing all {sketchfab_cards.length} models
                        </p>
                      )}
                    </>
                  ) : (
                    !sketchfab_loading && isSketchfab && (
                      <p className="text-[#555] text-[13px] text-center py-12">No Sketchfab results</p>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default LibrarySection;
